import type { TrackerCopy } from './types'

/** Contenido del tracker en español (mercado principal). */
export const TRACKER_COPY_ES: TrackerCopy = {

  meta: {
    title: '¿Dónde está Papá Noel? Mapa y cuenta atrás de Navidad',
    description:
      'Descubre cuánto falta para Navidad, por dónde va Papá Noel y a qué hora llegará. Sigue su recorrido en un mapa mundial interactivo.',
    keywords: [
      'donde esta papa noel',
      'cuanto falta para navidad',
      'cuanto falta para papa noel',
      'por donde va papa noel',
      'a que hora llega papa noel',
      'recorrido de papa noel',
      'contador de navidad',
      'cuantos dias faltan para navidad',
      'santa tracker',
      'cuenta atras navidad',
      'mapa papa noel',
      'seguir a papa noel',
    ],
    schemaName: 'Mapa y cuenta atrás de Papá Noel',
  },


  hero: {
    badge: {
      preparing: 'Taller del Polo Norte',
      liftoff: 'Últimos preparativos',
      traveling: 'Reparto en marcha',
      completed: 'Misión cumplida',
    },
    h1: {
      preparing: '¿Cuánto falta para que llegue Papá Noel?',
      liftoff: 'Papá Noel está a punto de despegar',
      traveling: '¿Dónde está Papá Noel ahora mismo?',
      completed: 'Papá Noel ya está de vuelta en el Polo Norte',
    },
    intro: {
      preparing:
        'En el Polo Norte no se para: los duendes envuelven regalos, los renos entrenan y la gran lista se revisa una y otra vez. Aquí puedes ver cuánto falta para Navidad y cómo avanzan los preparativos.',
      liftoff:
        'Faltan horas para el viaje más largo del año. El trineo ya está cargado, Rodolfo tiene la nariz encendida y el Polo Norte no pega ojo.',
      traveling:
        'Es Nochebuena y el trineo está dando la vuelta al mundo. Sigue su recorrido en el mapa, mira por dónde acaba de pasar y descubre cuándo se acercará a tu zona.',
      completed:
        'Todos los regalos están entregados y los renos descansan en el Polo Norte. La próxima Navidad ya está en marcha: aquí tienes la cuenta atrás.',
    },
    toMap: 'Ver el mapa',
  },

  countdown: {
    heading: {
      preparing: 'Papá Noel llegará a tu zona dentro de',
      liftoff: 'El trineo despega en',
      traveling: 'Llega a tu zona dentro de',
      completed: 'Para la próxima Navidad faltan',
    },
    units: { days: 'días', hours: 'horas', minutes: 'minutos', seconds: 'segundos' },
    christmasNote: 'La Navidad es el 25 de diciembre: faltan {days} días.',
    christmasToday: '¡Hoy es Navidad!',
    arrivingNow: '¡Está sobrevolando tu zona!',
    ariaLabel: 'Cuenta atrás para la llegada de Papá Noel',
  },

  status: {
    preparing: {
      far: [
        'Los duendes ya están preparando los primeros juguetes del año.',
        'Papá Noel está estrenando cuaderno para la gran lista.',
        'En el taller se prueban las ideas de regalos de la próxima Navidad.',
        'Los renos vuelven poco a poco de sus vacaciones.',
      ],
      months: [
        'El taller del Polo Norte trabaja sin descanso.',
        'Papá Noel está revisando su gran lista.',
        'Los renos han comenzado sus entrenamientos.',
        'Empiezan a llegar las primeras cartas del mundo entero.',
      ],
      weeks: [
        'La Señora Claus prepara galletas para todo el taller.',
        'Papá Noel repasa la lista por segunda vez.',
        'Los duendes envuelven regalos a toda velocidad.',
        'El trineo está en el taller, a punto para su revisión.',
      ],
      days: [
        '¡Ya queda muy poco para que despegue el trineo!',
        'Los duendes cierran los últimos sacos.',
        'Rodolfo está practicando para la gran noche.',
        'En el Polo Norte ya nadie duerme.',
      ],
      hours: [
        'Papá Noel está cargando los últimos regalos.',
        'El trineo está listo para despegar.',
        'Los renos ya están enganchados y esperando la señal.',
        'Rodolfo ya tiene encendida su nariz.',
      ],
    },
    liftoff: [
      'Papá Noel está cargando los últimos regalos.',
      'Rodolfo ya tiene encendida su nariz.',
      'Los duendes están cerrando el gran saco.',
      'El trineo está listo para despegar.',
    ],
    traveling: [
      'El trineo acaba de pasar por {city}.',
      'Papá Noel deja {city} con el saco un poco más ligero.',
      'Rodolfo guía el trineo tras la parada en {city}.',
      'En {city} ya han recibido sus regalos.',
    ],
    completed: [
      'Papá Noel ha terminado su recorrido.',
      'Todos los regalos han sido entregados.',
      'Los renos ya descansan en el Polo Norte.',
      'La próxima Navidad ya está en preparación.',
    ],
    lastVisited: 'Último lugar visitado',
    nextStop: 'Próximo destino',
    flyingTo: 'Volando hacia {leg}',
    atNorthPole: 'En el Polo Norte',
    backHome: 'De vuelta en casa',
    legLabel: {
      'north-pole': 'el Polo Norte',
      pacific: 'el Pacífico',
      oceania: 'Oceanía',
      'east-asia': 'Asia oriental',
      'southeast-asia': 'el sudeste asiático',
      'central-asia': 'Asia central y del sur',
      'middle-east': 'Oriente Medio',
      'eastern-europe': 'Europa oriental',
      africa: 'África',
      'western-europe': 'Europa occidental',
      'south-america': 'Sudamérica',
      'north-america': 'Norteamérica',
      'north-pole-return': 'el Polo Norte',
    },
  },

  map: {
    heading: 'El recorrido de Papá Noel',
    subheading: 'Toca el trineo o cualquier ciudad para saber más.',
    ariaLabel: 'Mapa del mundo con el recorrido de Papá Noel',
    sleighLabel: 'Trineo de Papá Noel',
    northPoleLabel: 'Polo Norte',
    legend: { visited: 'Ya ha pasado', current: 'Está aquí', upcoming: 'Todavía no' },
    stopStatus: {
      visited: 'Papá Noel ya ha pasado por aquí',
      current: 'Papá Noel está aquí ahora mismo',
      upcoming: 'Papá Noel todavía no ha llegado',
    },
    localTime: 'Hora local: {time}',
    close: 'Cerrar',
    followSleigh: 'Seguir al trineo',
    wholeWorld: 'Ver todo el mundo',
    listHeading: 'El recorrido, paso a paso',
    listIntro:
      'El mismo recorrido en forma de lista, por si prefieres leerlo o el mapa no se ve bien en tu pantalla.',
    parentsNote:
      'Nota para madres y padres: esta es una experiencia navideña simulada. El recorrido se calcula a partir de los husos horarios; no es una ubicación real ni un seguimiento por GPS.',
  },

  eta: {
    heading: '¿A qué hora llega a tu casa?',
    body: 'Papá Noel llegará a tu zona aproximadamente entre las {start} y las {end}.',
    zone: 'Calculado para tu zona horaria ({zone}).',
    remaining: 'Faltan {countdown}.',
    passed: 'Papá Noel ya ha pasado por tu zona. ¡Mira debajo del árbol!',
    unknownZone: 'Calculado con la hora de tu dispositivo.',
    disclaimer:
      'Es una franja aproximada, no una hora garantizada: Papá Noel prefiere pasar cuando todo el mundo duerme.',
  },

  stats: {
    heading: {
      preparing: 'Ahora mismo en el taller',
      liftoff: 'Todo listo para la gran noche',
      traveling: 'El viaje en cifras',
      completed: 'Así ha sido el viaje',
    },
    labels: {
      giftsReady: 'Regalos preparados',
      giftsDelivered: 'Regalos entregados',
      lettersReceived: 'Cartas recibidas',
      elvesWorking: 'Duendes trabajando',
      reindeerTraining: 'Renos entrenando',
      kilometers: 'Kilómetros',
      citiesVisited: 'Ciudades visitadas',
      countriesVisited: 'Países visitados',
      homesWaiting: 'Hogares esperando',
      travelMinutes: 'Tiempo de viaje',
    },
    units: { kilometers: 'km', hours: 'h' },
  },

  workshop: {
    heading: 'Ahora mismo en el Polo Norte',
    activities: {
      preparing: [
        'Los duendes están envolviendo regalos.',
        'Papá Noel está leyendo cartas.',
        'Los renos están entrenando en la pista de despegue.',
        'La Señora Claus está preparando galletas.',
        'El trineo está pasando su revisión anual.',
        'En la sala de la gran lista se comprueban los nombres.',
      ],
      liftoff: [
        'El trineo está pasando su última revisión.',
        'Rodolfo está practicando para la gran noche.',
        'Los duendes cargan los sacos en el trineo.',
        'La Señora Claus prepara chocolate caliente para el camino.',
      ],
      traveling: [
        'Los duendes siguen el viaje desde la torre de control.',
        'La Señora Claus deja la chimenea encendida esperando la vuelta.',
        'En el taller ya recogen los últimos papeles de regalo.',
        'Los renos de reserva esperan por si hace falta un relevo.',
      ],
      completed: [
        'Los renos descansan en el establo del Polo Norte.',
        'La Señora Claus sirve el chocolate de después del viaje.',
        'Los duendes ordenan el taller para el año que viene.',
        'Papá Noel guarda la gran lista hasta la próxima Navidad.',
      ],
    },
    night: [
      'En el Polo Norte es de noche: el taller trabaja a media luz.',
      'Los duendes del turno de noche envuelven en silencio.',
      'Papá Noel duerme un rato antes del gran día.',
      'Solo se oye el crujido de la nieve alrededor del taller.',
    ],
  },

  surprises: {
    northPole: '¡Has despertado la aurora boreal! Los duendes te saludan desde el taller.',
    celebration: '¡Recorrido completado! Feliz Navidad.',
  },

  controls: {
    soundOn: 'Activar sonido',
    soundOff: 'Silenciar',
    fullscreenEnter: 'Pantalla completa',
    fullscreenExit: 'Salir de pantalla completa',
    share: 'Compartir',
    shareCopied: 'Enlace copiado',
    motionOn: 'Activar animaciones',
    motionOff: 'Reducir movimiento',
  },

  share: {
    title: '¿Dónde está Papá Noel?',
    text: {
      preparing: 'Descubre cuánto falta para que llegue Papá Noel.',
      liftoff: '¡Papá Noel está a punto de despegar!',
      traveling: '¡Mira por dónde va Papá Noel!',
      completed: 'Papá Noel ya ha terminado su recorrido. ¡Feliz Navidad!',
    },
  },

  faq: {
    heading: 'Preguntas frecuentes',
    items: [
      {
        question: '¿Cuánto falta para Navidad?',
        answer:
          'La cuenta atrás de esta página te lo dice al segundo. La Navidad se celebra el 25 de diciembre, y Papá Noel sale del Polo Norte la noche del 24.',
      },
      {
        question: '¿Dónde está Papá Noel ahora?',
        answer:
          'Depende del día. La mayor parte del año está en el Polo Norte preparando la Navidad, y en Nochebuena recorre el mundo entero. El mapa de esta página muestra dónde le toca estar en cada momento.',
      },
      {
        question: '¿Por dónde va Papá Noel?',
        answer:
          'Empieza por el Pacífico, sigue por Oceanía y Asia, cruza Oriente Medio, Europa y África, baja a Sudamérica y termina en Norteamérica antes de volver a casa. Va persiguiendo la noche: reparte donde ya está oscuro.',
      },
      {
        question: '¿Cuándo sale Papá Noel del Polo Norte?',
        answer:
          'Despega la tarde del 24 de diciembre, cuando en las islas del Pacífico ya casi es medianoche. Desde ahí no para hasta la mañana del 25.',
      },
      {
        question: '¿A qué hora llega Papá Noel?',
        answer:
          'A cada casa llega cuando los niños ya están dormidos, más o menos entre las nueve de la noche y la medianoche. En esta página verás la franja aproximada calculada para tu zona horaria.',
      },
      {
        question: '¿Papá Noel pasa por todos los países?',
        answer:
          'Sí: los 195 países del mundo entran en el recorrido. En el mapa se marcan algunas ciudades como referencia, pero el trineo pasa por todas partes.',
      },
      {
        question: '¿Cómo puede recorrer el mundo en una sola noche?',
        answer:
          'Porque viaja hacia el oeste, persiguiendo la noche: al ir en la misma dirección que gira la Tierra, la Nochebuena le dura más de treinta horas. Eso, y un poco de magia del Polo Norte.',
      },
      {
        question: '¿Qué pasa si estoy despierto cuando llega?',
        answer:
          'Papá Noel es muy discreto: si nota que alguien está despierto, sigue su camino y vuelve más tarde. Lo mejor para que pase pronto es irse a la cama temprano.',
      },
      {
        question: '¿Cómo sabe Papá Noel dónde vivo?',
        answer:
          'Por las cartas. Cada carta que llega al Polo Norte se anota en la gran lista con el nombre y la ciudad, y los duendes preparan la ruta con esa información.',
      },
      {
        question: '¿Cuándo vuelve al Polo Norte?',
        answer:
          'La mañana del 25 de diciembre, después de la última entrega. Los renos descansan unos días y el taller empieza a pensar en la Navidad siguiente.',
      },
      {
        question: '¿El mapa muestra su ubicación real?',
        answer:
          'No. Es una experiencia navideña simulada: el recorrido se calcula a partir de los husos horarios para que encaje con la noche de cada lugar. No hay seguimiento por GPS ni ubicación real, y esta página nunca pide tu localización.',
      },
      {
        question: '¿Cómo puedo hablar con Papá Noel?',
        answer:
          'En PapaNoel.chat puedes preparar una llamada en la que Papá Noel habla directamente con tu hijo o hija, por su nombre y en tiempo real. Tú decides qué sabe de antemano.',
      },
      {
        question: '¿Puedo hacer una videollamada con Papá Noel?',
        answer:
          'Sí. Además de la llamada de voz, puedes hacer una videollamada personalizada en la que Papá Noel conoce el nombre, los gustos, los logros y los deseos de tu hijo o hija.',
      },
    ],
  },

  cta: {
    heading: 'Mientras esperas su llegada, puedes hablar con Papá Noel',
    body: 'Prepara una videollamada personalizada en la que Papá Noel conocerá el nombre, los gustos, los logros y los deseos de tu hijo o hija.',
    primary: 'Hablar con Papá Noel',
    secondary: 'Descubrir personajes mágicos',
    free: '¿Queréis probar antes? Habla gratis con Papá Noel',
    travelingNote:
      'Papá Noel está muy ocupado esta noche, pero todavía puedes preparar una videollamada mágica.',
  },

  stops: {
    'north-pole': 'Polo Norte',
    'north-pole-return': 'Polo Norte',
    auckland: 'Auckland',
    suva: 'Suva',
    sydney: 'Sídney',
    melbourne: 'Melbourne',
    brisbane: 'Brisbane',
    'port-moresby': 'Port Moresby',
    darwin: 'Darwin',
    tokyo: 'Tokio',
    seoul: 'Seúl',
    shanghai: 'Shanghái',
    beijing: 'Pekín',
    'hong-kong': 'Hong Kong',
    manila: 'Manila',
    singapore: 'Singapur',
    jakarta: 'Yakarta',
    bangkok: 'Bangkok',
    dhaka: 'Daca',
    kathmandu: 'Katmandú',
    'new-delhi': 'Nueva Delhi',
    karachi: 'Karachi',
    kabul: 'Kabul',
    dubai: 'Dubái',
    tehran: 'Teherán',
    riyadh: 'Riad',
    istanbul: 'Estambul',
    moscow: 'Moscú',
    athens: 'Atenas',
    warsaw: 'Varsovia',
    cairo: 'El Cairo',
    johannesburg: 'Johannesburgo',
    lagos: 'Lagos',
    berlin: 'Berlín',
    rome: 'Roma',
    paris: 'París',
    madrid: 'Madrid',
    london: 'Londres',
    lisbon: 'Lisboa',
    reykjavik: 'Reikiavik',
    'rio-de-janeiro': 'Río de Janeiro',
    'sao-paulo': 'São Paulo',
    'buenos-aires': 'Buenos Aires',
    santiago: 'Santiago de Chile',
    'la-paz': 'La Paz',
    caracas: 'Caracas',
    bogota: 'Bogotá',
    lima: 'Lima',
    'new-york': 'Nueva York',
    toronto: 'Toronto',
    havana: 'La Habana',
    'mexico-city': 'Ciudad de México',
    chicago: 'Chicago',
    denver: 'Denver',
    'los-angeles': 'Los Ángeles',
    vancouver: 'Vancouver',
    anchorage: 'Anchorage',
    honolulu: 'Honolulu',
  },
}
